import React from 'react';
import { Solicitud } from '@/types/solicitud';
import { Empresa } from '@/types/empresa';
import { Button } from '@/components/ui/Button';
import { usePlazosDisponibles } from '@/hooks/usePlazosDisponibles';
import { MontoInput } from '@/components/ui/MontoInput';
import { useAuth } from '@/hooks/useAuth';

interface PreAnalisisSectionProps {
  localSolicitud: Solicitud;
  setLocalSolicitud: React.Dispatch<React.SetStateAction<Solicitud | null>>;
  setHasChanges: (v: boolean) => void;
  error: string | null;
  empresas: Empresa[];
  isRunningAnalisis: boolean;
  handleRealizarAnalisis: () => Promise<void>;
  handleAceptarPropuesta: (tipoPropuesta: string) => Promise<void>;
  hasDatosCalculados: boolean;
  acceptingPropuesta: string | null;
  successMsg: string | null;
}

const fmt = (n: number) => `$${new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2 }).format(n)}`;

function SimuladorCard({ p, title, headerClass, showFactibilidad, tipoBuro, tipoListaNegra, onAceptar, isLoading, clienteNombre }: {
  p: Record<string, unknown>;
  title: string;
  headerClass: string;
  showFactibilidad?: boolean;
  tipoBuro?: string;
  tipoListaNegra?: string;
  onAceptar?: () => void;
  isLoading?: boolean;
  clienteNombre?: string;
}) {
  const { isFieldAllowed } = useAuth();
  const canSeeNumeroPagos = isFieldAllowed('numeroPagos');
  const canSeeMontoCapacidadDePago = isFieldAllowed('montoCapacidadDePago');
  const canSeePagoEstimadoPeriodo = isFieldAllowed('pagoEstimadoPeriodo');
  const canSeePagoTotalPeriodo = isFieldAllowed('pagoTotalPeriodo');
  const canSeeCapacidadDePago = isFieldAllowed('capacidadDePago');

  const factibilidad = String(p.factibilidad || '');
  const procede = factibilidad.startsWith('Sí Procede') || factibilidad === 'Si Procede';
  const seguroMonto = Number(p.montoSeguro) || 0;
  const tipoPagoSeguro = String(p.tipoPagoSeguro || 'N/A');
  const capacidadOk = p.capacidadDePago === 'Ok';

  // Condiciones estilo legacy: tipoBuroCredito != "Malo" → Ok
  // Legacy: solo muestra alerta roja si tipoBuro es exactamente "Malo"
  const buroOk = !tipoBuro || tipoBuro !== 'Malo';
  // Legacy: tipoListaNegra == "Sin Resultados" o "" → Ok
  const listasOk = !tipoListaNegra || tipoListaNegra === '' || tipoListaNegra === 'No Aplica' || tipoListaNegra === 'Sin Resultados';

  const Row = ({ label, value, cls }: { label: string; value: string; cls?: string }) => (
    <div className="flex justify-between py-0.5 border-b border-gray-100 last:border-0">
      <span className="text-gray-500 text-xs">{label}</span>
      <span className={`text-xs font-medium text-right ${cls || 'text-gray-900'}`}>{value}</span>
    </div>
  );

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden flex flex-col">
      <div className={`px-3 py-2 font-bold text-sm ${headerClass}`}>
        {title}
      </div>
      <div className="p-3 flex-1 space-y-0">
        {/* Cliente - como legacy */}
        {clienteNombre && (
          <div className="text-center text-xs font-medium text-gray-700 py-1 mb-1 border-b border-gray-200">
            {clienteNombre}
          </div>
        )}

        {/* Factibilidad arriba - como legacy */}
        {showFactibilidad && (
          <div className={`text-center text-xs font-bold py-1.5 mb-2 rounded ${procede ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {factibilidad}
          </div>
        )}

        <Row label="Monto del contrato" value={fmt(Number(p.montoAutorizado) || 0)} />
        <Row label="Monto Transferencia" value={fmt(Math.max(0, Number(p.montoTransferencia) || 0))} />
        <Row label="Plazo en meses" value={String(p.plazoAutorizado || '0')} />
        <Row label="Fecha de contrato" value={String(p.fechaEmisionContrato || '\u2014')} />
        <Row label="Primer pago" value={String(p.fechaPrimerCobro || '\u2014')} />
        <Row label="Fecha del último pago" value={String(p.fechaUltimoPago || '\u2014')} />
        {canSeeNumeroPagos && <Row label="No. Amortizaciones" value={String(p.numeroPagos || 0)} />}
        <Row label="Frecuencia de pago" value={String(p.frecuenciaDePago || '\u2014')} />
        <Row label={`Seguro - ${tipoPagoSeguro}`} value={fmt(seguroMonto)} />
        <Row label="% Comisión Financiada" value={`${Number(p.tasaDeComision) || 0}%`} />
        <Row label="Comisión Financiada" value={fmt(Number(p.montoComision) || 0)} />
        <Row label="IVA Comisión Financiada" value={fmt(Number(p.montoIvaComision) || 0)} />
        {canSeePagoEstimadoPeriodo && <Row label="Pago por Parcialidad" value={fmt(Number(p.pagoParcialidadSimulador) || 0)} />}
        {(canSeeMontoCapacidadDePago || canSeeCapacidadDePago) && (
          <Row label="Capacidad de Pago" value={String(p.capacidadDePago || '\u2014')} cls={capacidadOk ? 'text-green-700' : 'text-red-700'} />
        )}

        <div className="border-t border-gray-300 mt-1 pt-1">
          <Row label="Total Pago de capital" value={fmt(Number(p.montoMinistracion) || 0)} />
          <Row label="Total Pago de Intereses" value={fmt(Number(p.totalInteresesSimulador) || 0)} />
          <Row label="Total Pago IVA" value={fmt(Number(p.ivaInteresesSimulador) || 0)} />
          {canSeePagoTotalPeriodo && (
          <div className="flex justify-between py-1 mt-1 bg-gray-50 rounded px-1">
            <span className="text-gray-700 text-sm font-bold">Pago Total</span>
            <span className="text-sm font-bold text-gray-900">{fmt(Number(p.pagoTotalSimulador) || 0)}</span>
          </div>
          )}
        </div>

        {/* Condiciones estilo legacy */}
        <div className="mt-2 space-y-1">
          <div className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded ${capacidadOk ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            <span>{capacidadOk ? '\u2713' : '\u2717'}</span>
            <span>{capacidadOk ? 'CUENTA CON CAPACIDAD DE PAGO' : 'NO CUENTA CON CAPACIDAD DE PAGO'}</span>
          </div>
          <div className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded ${buroOk && listasOk ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            <span>{buroOk && listasOk ? '\u2713' : '\u2717'}</span>
            <span>{buroOk && listasOk ? 'Buró y Listas Negras están Ok' : `Buró: ${tipoBuro || 'N/A'} | Listas: ${tipoListaNegra || 'N/A'}`}</span>
          </div>
        </div>

        {/* Boton Aceptar Propuesta */}
        {onAceptar && (
          <div className="mt-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onAceptar}
              disabled={isLoading}
              className="w-full py-2 px-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded transition-colors"
            >
              {isLoading ? 'Aceptando...' : 'Aceptar Propuesta'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function PreAnalisisSection({
  localSolicitud,
  setLocalSolicitud,
  setHasChanges,
  error,
  empresas,
  isRunningAnalisis,
  handleRealizarAnalisis,
  handleAceptarPropuesta,
  hasDatosCalculados,
  acceptingPropuesta,
  successMsg,
}: PreAnalisisSectionProps) {
  const { isFieldAllowed } = useAuth();
  const { plazos: plazosOptions } = usePlazosDisponibles();
  const canEditRegion = isFieldAllowed('regionNombre');
  const canEditClienteEmpresa = isFieldAllowed('clienteEmpresa');
  const canEditClienteNombre = isFieldAllowed('clienteNombre');
  const canEditClienteApellidoPaterno = isFieldAllowed('clienteApellidoPaterno');
  const canEditClienteApellidoMaterno = isFieldAllowed('clienteApellidoMaterno');
  const canEditClienteDirectivo = isFieldAllowed('clienteDirectivo');
  const canEditClienteSindicalizado = isFieldAllowed('clienteSindicalizado');
  const canEditClienteFechaIngreso = isFieldAllowed('clienteFechaIngreso');
  const canEditFrecuenciaDePago = isFieldAllowed('frecuenciaDePago');
  const canEditClienteSalario = isFieldAllowed('clienteSalario');
  const canEditClienteDeduccionesMensuales = isFieldAllowed('clienteDeduccionesMensuales');
  const canEditClienteDeduccionesQuincenales = isFieldAllowed('clienteDeduccionesQuincenales');
  const canEditClienteDeduccionesSemanales = isFieldAllowed('clienteDeduccionesSemanales');
  const canEditProducto = isFieldAllowed('producto');
  const canEditMontoSolicitado = isFieldAllowed('montoSolicitado');
  const canEditPlazoSolicitado = isFieldAllowed('plazoSolicitado');
  const canEditPolizaSeguro = isFieldAllowed('polizaSeguro');
  const canEditSolicitudEsCleanUp = isFieldAllowed('solicitudEsCleanUp');
  const canEditSolicitudSaldoParcialidadesMontoAnterior = isFieldAllowed('solicitudSaldoParcialidadesMontoAnterior');
  const canEditTipoListaNegra = isFieldAllowed('tipoListaNegra');
  const canEditSaldo1a89 = isFieldAllowed('saldo1a89');
  const canEditSaldoMayor90 = isFieldAllowed('saldoMayor90');
  const canEditSaldoMayor120 = isFieldAllowed('saldoMayor120');
  const canEditSolicitudMop = isFieldAllowed('solicitudMop');
  const canSeeMontoMaximoPolitica = isFieldAllowed('montoMaximoPolitica');
  const canSeeMontoPropuesto = isFieldAllowed('montoPropuesto');
  const canSeePlazoMaximoPolitica = isFieldAllowed('plazoMaximoPolitica');
  const canSeeTasaDeComisionPolitica = isFieldAllowed('tasaDeComisionPolitica');
  const canSeeMontoComisionPolitica = isFieldAllowed('montoComisionPolitica');
  const canSeeTasaPolitica = isFieldAllowed('tasaPolitica');
  const canSeeDocumentos = isFieldAllowed('documentos');
  const canAddArchivos = isFieldAllowed('addArchivos');
  const canListaArchivos = isFieldAllowed('listaArchivos');
  const canEditFechaEmisionContrato = isFieldAllowed('fechaEmisionContrato');
  const canEditFechaPrimerCobro = isFieldAllowed('fechaPrimerCobro');
  const canAccionRealizarAnalisis = isFieldAllowed('accionRealizarAnalisis');
  const canSeeComentarios = isFieldAllowed('comentarios');
  const canEditComentarios = isFieldAllowed('comentarios');

  const isAdelantoNomina = localSolicitud.producto === 'Adelanto de Nómina';

  const pSol = (hasDatosCalculados && localSolicitud.propuestaSolicitud && typeof localSolicitud.propuestaSolicitud === 'object' && Object.keys(localSolicitud.propuestaSolicitud).length > 0)
    ? localSolicitud.propuestaSolicitud as Record<string, unknown>
    : null;

  const pBB = (hasDatosCalculados && localSolicitud.propuestaBuenBuro && typeof localSolicitud.propuestaBuenBuro === 'object' && Object.keys(localSolicitud.propuestaBuenBuro).length > 0)
    ? localSolicitud.propuestaBuenBuro as Record<string, unknown>
    : null;

  const pRec = (hasDatosCalculados && localSolicitud.propuestaRecomendacion && typeof localSolicitud.propuestaRecomendacion === 'object' && Object.keys(localSolicitud.propuestaRecomendacion).length > 0)
    ? localSolicitud.propuestaRecomendacion as Record<string, unknown>
    : null;

  const hasAnyPropuesta = pSol || pBB || pRec;

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}
      {/* Seccion 1: Datos de la solicitud (editable, formato tabla) */}
      <table className="w-full border-collapse text-sm">
        <tbody>
          <tr className="bg-gray-100">
            <td colSpan={2} className="px-3 py-2 font-bold text-gray-700 border border-gray-300">Datos de la Solicitud</td>
          </tr>
          {canEditRegion && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200 w-1/3">Region</td>
            <td className="px-3 py-2 border border-gray-200">
              <input
                type="text"
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.regionNombre || ''}
                onChange={(e) => { setLocalSolicitud({ ...localSolicitud, regionNombre: e.target.value }); setHasChanges(true); }}
              />
            </td>
          </tr>
          )}
          {canEditClienteEmpresa && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Empresa</td>
            <td className="px-3 py-2 border border-gray-200">
              <select
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.clienteEmpresa || ''}
                onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteEmpresa: e.target.value }); setHasChanges(true); }}
              >
                <option value="">Seleccionar empresa...</option>
                {empresas.map(empresa => (
                  <option key={empresa.id || empresa.nombre} value={empresa.nombre}>
                    {empresa.nombre}
                  </option>
                ))}
              </select>
            </td>
          </tr>
          )}
          {canEditClienteNombre && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Nombre</td>
            <td className="px-3 py-2 border border-gray-200">
              <input
                type="text"
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.clienteNombre || ''}
                onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteNombre: e.target.value }); setHasChanges(true); }}
              />
            </td>
          </tr>
          )}
          {canEditClienteApellidoPaterno && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Apellido Paterno</td>
            <td className="px-3 py-2 border border-gray-200">
              <input
                type="text"
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.clienteApellidoPaterno || ''}
                onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteApellidoPaterno: e.target.value }); setHasChanges(true); }}
              />
            </td>
          </tr>
          )}
          {canEditClienteApellidoMaterno && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Apellido Materno</td>
            <td className="px-3 py-2 border border-gray-200">
              <input
                type="text"
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.clienteApellidoMaterno || ''}
                onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteApellidoMaterno: e.target.value }); setHasChanges(true); }}
              />
            </td>
          </tr>
          )}
          {canEditProducto && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Producto</td>
            <td className="px-3 py-2 border border-gray-200">
              <select
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.producto || ''}
                onChange={(e) => { setLocalSolicitud({ ...localSolicitud, producto: e.target.value }); setHasChanges(true); }}
              >
                <option value="">Seleccionar...</option>
                <option value="Nómina">Nómina</option>
                <option value="Adelanto de Nómina">Adelanto de Nómina</option>
                <option value="Enganche Hipotecario">Enganche Hipotecario</option>
              </select>
            </td>
          </tr>
          )}
          {canEditMontoSolicitado && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Solicitado ($)</td>
            <td className="px-3 py-2 border border-gray-200">
              <MontoInput
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.montoSolicitado}
                onChange={(n) => { setLocalSolicitud({ ...localSolicitud, montoSolicitado: n }); setHasChanges(true); }}
              />
            </td>
          </tr>
          )}
          {canEditPlazoSolicitado && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Plazo Solicitado (meses)</td>
            <td className="px-3 py-2 border border-gray-200">
              <select
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={localSolicitud.plazoSolicitado || ''}
                onChange={(e) => { setLocalSolicitud({ ...localSolicitud, plazoSolicitado: e.target.value }); setHasChanges(true); }}
              >
                <option value="">Seleccionar...</option>
                {plazosOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </td>
          </tr>
          )}
          {canSeeDocumentos && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Link Documentos</td>
            <td className="px-3 py-2 border border-gray-200">
              <div className="flex gap-2">
                {canAddArchivos ? (
                  <input
                    type="text"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    value={localSolicitud.documentos || ''}
                    onChange={(e) => { setLocalSolicitud({ ...localSolicitud, documentos: e.target.value }); setHasChanges(true); }}
                    placeholder="https://drive.google.com/..."
                  />
                ) : (
                  <input type="text" className="flex-1 px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.documentos || ''} readOnly />
                )}
                {canListaArchivos && localSolicitud.documentos && (
                  <button
                    type="button"
                    className="px-3 py-1 border border-gray-400 rounded bg-gray-100 hover:bg-gray-200 text-xs whitespace-nowrap"
                    onClick={() => window.open(localSolicitud.documentos, '_blank')}
                  >
                    Ver
                  </button>
                )}
              </div>
            </td>
          </tr>
          )}
          {canSeeComentarios && (
          <tr>
            <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Comentarios</td>
            <td className="px-3 py-2 border border-gray-200">
              {canEditComentarios ? (
                <textarea
                  className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  rows={3}
                  value={localSolicitud.comentarios || ''}
                  onChange={(e) => { setLocalSolicitud({ ...localSolicitud, comentarios: e.target.value }); setHasChanges(true); }}
                />
              ) : (
                <textarea
                  className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50"
                  rows={3}
                  value={localSolicitud.comentarios || ''}
                  readOnly
                />
              )}
            </td>
          </tr>
          )}
        </tbody>
      </table>

      {/* Seccion 2: Formulario de Pre Analisis (editable) */}
      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
        <h4 className="text-sm font-bold text-amber-900 mb-3">Formulario de Pre Analisis</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {canEditPolizaSeguro && !isAdelantoNomina && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Poliza de Seguro</label>
            <select
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.polizaSeguro || ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, polizaSeguro: e.target.value }); setHasChanges(true); }}
            >
              <option value="">Seleccionar...</option>
              <option value="Ninguna">Ninguna</option>
              <option value="25000">$25,000</option>
              <option value="50000">$50,000</option>
              <option value="75000">$75,000</option>
              <option value="100000">$100,000</option>
            </select>
          </div>
          )}
          {canEditClienteDirectivo && !isAdelantoNomina && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Es Director</label>
            <select
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.clienteDirectivo === true ? 'Si' : localSolicitud.clienteDirectivo === false ? 'No' : ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteDirectivo: e.target.value === 'Si' ? true : e.target.value === 'No' ? false : undefined }); setHasChanges(true); }}
            >
              <option value="">Seleccionar...</option>
              <option value="Si">Si</option>
              <option value="No">No</option>
            </select>
          </div>
          )}
          {canEditClienteSindicalizado && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Sindicalizado</label>
            <select
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.clienteSindicalizado || ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteSindicalizado: e.target.value }); setHasChanges(true); }}
            >
              <option value="">Seleccionar...</option>
              <option value="No Aplica">No Aplica</option>
              <option value="Auxiliar Caabsa Eagle">Auxiliar Caabsa Eagle</option>
              <option value="Chofer Caabsa Eagle">Chofer Caabsa Eagle</option>
            </select>
          </div>
          )}
          {canEditClienteFechaIngreso && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Fecha de Ingreso</label>
            <input
              type="date"
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={String(localSolicitud.clienteFechaIngreso || '').split('T')[0].split(' ')[0]}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteFechaIngreso: e.target.value }); setHasChanges(true); }}
            />
          </div>
          )}
          {canEditFrecuenciaDePago && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Frecuencia de Pago</label>
            <select
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.frecuenciaDePago || ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, frecuenciaDePago: e.target.value }); setHasChanges(true); }}
            >
              <option value="">Seleccionar...</option>
              <option value="Semanal">Semanal</option>
              <option value="Quincenal">Quincenal</option>
              <option value="Catorcenal">Catorcenal</option>
              <option value="Mensual">Mensual</option>
            </select>
          </div>
          )}
          {canEditClienteSalario && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Ingreso Bruto Mensual</label>
            <input
              type="number"
              step="0.01"
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.clienteSalario ?? ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteSalario: e.target.value ? Number(e.target.value) : undefined }); setHasChanges(true); }}
            />
          </div>
          )}
          {canEditClienteDeduccionesMensuales && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Deducciones Mensuales</label>
            <input
              type="number"
              step="0.01"
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.clienteDeduccionesMensuales ?? ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteDeduccionesMensuales: e.target.value ? Number(e.target.value) : undefined }); setHasChanges(true); }}
            />
          </div>
          )}
          {canEditClienteDeduccionesQuincenales && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Deducciones Quincenales</label>
            <input
              type="number"
              step="0.01"
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.clienteDeduccionesQuincenales ?? ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteDeduccionesQuincenales: e.target.value ? Number(e.target.value) : undefined }); setHasChanges(true); }}
            />
          </div>
          )}
          {canEditClienteDeduccionesSemanales && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Deducciones Semanales</label>
            <input
              type="number"
              step="0.01"
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.clienteDeduccionesSemanales ?? ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteDeduccionesSemanales: e.target.value ? Number(e.target.value) : undefined }); setHasChanges(true); }}
            />
          </div>
          )}
          {canEditTipoListaNegra && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Listas Negras Tipo</label>
            <select
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.tipoListaNegra || ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, tipoListaNegra: e.target.value }); setHasChanges(true); }}
            >
              <option value="">Seleccionar...</option>
              <option value="No Aplica">No Aplica</option>
              <option value="Sin Resultados">Sin Resultados</option>
              <option value="Es PEP">Es PEP</option>
              <option value="Persona Bloqueada">Persona Bloqueada</option>
              <option value="69 o 69 Bis">69 o 69 Bis</option>
              <option value="Listas Negras Internacionales">Listas Negras Internacionales</option>
              <option value="Es Proceso Juridico">Es Proceso Juridico</option>
            </select>
          </div>
          )}
          {canEditSaldo1a89 && !isAdelantoNomina && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Saldo de 1-89 dias</label>
            <input
              type="number"
              step="0.01"
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.saldo1a89 ?? ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, saldo1a89: e.target.value ? Number(e.target.value) : undefined }); setHasChanges(true); }}
            />
          </div>
          )}
          {canEditSaldoMayor90 && !isAdelantoNomina && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Saldo con 90-120 dias</label>
            <input
              type="number"
              step="0.01"
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.saldoMayor90 ?? ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, saldoMayor90: e.target.value ? Number(e.target.value) : undefined }); setHasChanges(true); }}
            />
          </div>
          )}
          {canEditSaldoMayor120 && !isAdelantoNomina && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Saldo mayor a 120 dias</label>
            <input
              type="number"
              step="0.01"
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.saldoMayor120 ?? ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, saldoMayor120: e.target.value ? Number(e.target.value) : undefined }); setHasChanges(true); }}
            />
          </div>
          )}
          {canEditSolicitudMop && !isAdelantoNomina && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">MOP mas alto</label>
            <select
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.solicitudMop || ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, solicitudMop: e.target.value }); setHasChanges(true); }}
            >
              <option value="">Seleccionar...</option>
              <option value="00">00</option>
              <option value="01">01</option>
              <option value="02">02</option>
              <option value="03">03</option>
              <option value="04">04</option>
              <option value="05">05</option>
              <option value="06">06</option>
              <option value="07">07</option>
              <option value="96">96</option>
              <option value="97">97</option>
            </select>
          </div>
          )}
          {canEditSolicitudEsCleanUp && !isAdelantoNomina && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Es Clean Up</label>
            <select
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.solicitudEsCleanUp === true ? 'Si' : localSolicitud.solicitudEsCleanUp === false ? 'No' : ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, solicitudEsCleanUp: e.target.value === 'Si' ? true : e.target.value === 'No' ? false : undefined }); setHasChanges(true); }}
            >
              <option value="">Seleccionar...</option>
              <option value="Si">Si</option>
              <option value="No">No</option>
            </select>
          </div>
          )}
          {canEditSolicitudSaldoParcialidadesMontoAnterior && !isAdelantoNomina && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Total por cobrar SIAC</label>
            <input
              type="number"
              step="0.01"
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={localSolicitud.solicitudSaldoParcialidadesMontoAnterior ?? ''}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, solicitudSaldoParcialidadesMontoAnterior: e.target.value ? Number(e.target.value) : undefined }); setHasChanges(true); }}
            />
          </div>
          )}
          {canEditFechaEmisionContrato && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Fecha de Emision de Contrato</label>
            <input
              type="date"
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={String(localSolicitud.fechaEmisionContrato || '').split('T')[0].split(' ')[0]}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, fechaEmisionContrato: e.target.value }); setHasChanges(true); }}
            />
          </div>
          )}
          {canEditFechaPrimerCobro && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">Fecha de Primer Cobro</label>
            <input
              type="date"
              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              value={String(localSolicitud.fechaPrimerCobro || '').split('T')[0].split(' ')[0]}
              onChange={(e) => { setLocalSolicitud({ ...localSolicitud, fechaPrimerCobro: e.target.value }); setHasChanges(true); }}
            />
          </div>
          )}
        </div>
        {canAccionRealizarAnalisis && (
        <div className="mt-4">
          <Button
            variant="primary"
            onClick={handleRealizarAnalisis}
            disabled={isRunningAnalisis}
          >
            {isRunningAnalisis ? 'Ejecutando analisis...' : 'Realizar Analisis'}
          </Button>
        </div>
        )}
      </div>

      {/* Seccion 3: Datos calculados del analisis (leidos de BD) */}
      {hasDatosCalculados && (() => {
        const pagadora = localSolicitud.clientePagadora || '';
        const vobo = localSolicitud.voboRequerido || '';
        const antiguedad = localSolicitud.clienteAntiguedadMeses ?? 0;
        const tipoBuro = localSolicitud.tipoBuroCredito || '';
        const capacidad = localSolicitud.montoCapacidadDePago ?? 0;
        return (
        <table className="w-full border-collapse text-sm">
          <tbody>
            <tr className="bg-blue-100">
              <td colSpan={2} className="px-3 py-2 font-bold text-blue-800 border border-blue-300">Pre-analisis</td>
            </tr>
            <tr>
              <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200 w-1/3">Pagadora</td>
              <td className="px-3 py-2 border border-gray-200">
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={pagadora || '\u2014 Sin pagadora'} readOnly />
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Requiere VoBo</td>
              <td className="px-3 py-2 border border-gray-200">
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={vobo || '\u2014'} readOnly />
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Antiguedad (meses)</td>
              <td className="px-3 py-2 border border-gray-200">
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={String(antiguedad)} readOnly />
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Buro de Credito</td>
              <td className="px-3 py-2 border border-gray-200">
                <input
                  type="text"
                  className={`w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 font-medium ${
                    tipoBuro === 'Bueno' ? 'text-green-700' :
                    tipoBuro === 'Regular con Capacidad' ? 'text-blue-700' :
                    tipoBuro === 'Regular' ? 'text-amber-700' :
                    tipoBuro === 'Malo' ? 'text-red-700' : 'text-gray-700'
                  }`}
                  value={tipoBuro || '\u2014 Sin datos suficientes'}
                  readOnly
                />
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Capacidad de Pago</td>
              <td className="px-3 py-2 border border-gray-200">
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 font-medium" value={fmt(capacidad)} readOnly />
              </td>
            </tr>
            {canSeeMontoMaximoPolitica && (
            <tr>
              <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Maximo Politica</td>
              <td className="px-3 py-2 border border-gray-200">
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 font-medium" value={fmt(localSolicitud.montoMaximoPolitica || 0)} readOnly />
              </td>
            </tr>
            )}
            <tr>
              <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Oferta por Buen Buro</td>
              <td className="px-3 py-2 border border-gray-200">
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 font-medium" value={fmt(localSolicitud.montoBuenBuro || 0)} readOnly />
              </td>
            </tr>
            {canSeeMontoPropuesto && (
            <tr>
              <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Propuesto</td>
              <td className="px-3 py-2 border border-gray-200">
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 font-medium" value={fmt(localSolicitud.montoPropuesto || 0)} readOnly />
              </td>
            </tr>
            )}
            {canSeeTasaPolitica && (
            <tr>
              <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Tasa por Politica %</td>
              <td className="px-3 py-2 border border-gray-200">
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 font-medium" value={`${localSolicitud.tasaPolitica || 0}%`} readOnly />
              </td>
            </tr>
            )}
            {canSeePlazoMaximoPolitica && (
            <tr>
              <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Plazo Maximo Politica (meses)</td>
              <td className="px-3 py-2 border border-gray-200">
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 font-medium" value={localSolicitud.plazoMaximoPolitica || '0'} readOnly />
              </td>
            </tr>
            )}
            {canSeeTasaDeComisionPolitica && (
            <tr>
              <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Tasa Comision Politica %</td>
              <td className="px-3 py-2 border border-gray-200">
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 font-medium" value={`${localSolicitud.tasaDeComisionPolitica || 0}%`} readOnly />
              </td>
            </tr>
            )}
            {canSeeMontoComisionPolitica && (
            <tr>
              <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Comision Politica</td>
              <td className="px-3 py-2 border border-gray-200">
                <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 font-medium" value={fmt(localSolicitud.montoComisionPolitica || 0)} readOnly />
              </td>
            </tr>
            )}
          </tbody>
        </table>
        );
      })()}

      {successMsg && (
        <div className="p-3 rounded border border-green-200 bg-green-50 text-green-700 text-sm font-medium">
          {successMsg}
        </div>
      )}

      {/* Seccion 4: Simuladores lado a lado */}
      {hasDatosCalculados && hasAnyPropuesta && (() => {
        const clienteNombreCompleto = [
          localSolicitud.clienteNombre,
          localSolicitud.clienteApellidoPaterno,
          localSolicitud.clienteApellidoMaterno
        ].filter(Boolean).join(' ') || undefined;

        return (
        <div className={`grid gap-3 ${
          [pSol, pBB, pRec].filter(Boolean).length === 1 ? 'grid-cols-1' :
          [pSol, pBB, pRec].filter(Boolean).length === 2 ? 'grid-cols-1 lg:grid-cols-2' :
          'grid-cols-1 lg:grid-cols-3'
        }`}>
          {pSol && (
            <SimuladorCard
              p={pSol}
              title="Condiciones Solicitadas"
              headerClass={(() => {
                const f = String(pSol.factibilidad || '');
                return (f.startsWith('Sí Procede') || f === 'Si Procede')
                  ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
              })()}
              showFactibilidad
              tipoBuro={localSolicitud.tipoBuroCredito || ''}
              tipoListaNegra={localSolicitud.tipoListaNegra || ''}
              onAceptar={() => handleAceptarPropuesta('Solicitud')}
              isLoading={acceptingPropuesta === 'Solicitud'}
              clienteNombre={clienteNombreCompleto}
            />
          )}
          {pBB && (
            <SimuladorCard
              p={pBB}
              title={localSolicitud.tipoBuroCredito === 'Regular con Capacidad'
                ? 'Simulador por Buró Regular con Capacidad'
                : 'Oferta por Buen Buró'}
              headerClass={localSolicitud.tipoBuroCredito === 'Regular con Capacidad'
                ? 'bg-yellow-200 text-yellow-900'
                : 'bg-emerald-100 text-emerald-800'}
              tipoBuro={localSolicitud.tipoBuroCredito || ''}
              tipoListaNegra={localSolicitud.tipoListaNegra || ''}
              onAceptar={() => handleAceptarPropuesta('BuenBuro')}
              isLoading={acceptingPropuesta === 'BuenBuro'}
              clienteNombre={clienteNombreCompleto}
            />
          )}
          {pRec && (
            <SimuladorCard
              p={pRec}
              title="Mejor Propuesta"
              headerClass="bg-violet-100 text-violet-800"
              tipoBuro={localSolicitud.tipoBuroCredito || ''}
              tipoListaNegra={localSolicitud.tipoListaNegra || ''}
              onAceptar={() => handleAceptarPropuesta('Recomendacion')}
              isLoading={acceptingPropuesta === 'Recomendacion'}
              clienteNombre={clienteNombreCompleto}
            />
          )}
        </div>
        );
      })()}

    </div>
  );
}
